import React, { useState } from 'react';
import { UserPlus, Check, X, Eye, EyeOff } from 'lucide-react';
import api from '../utils/api';
import TEXT_CONSTANTS from '../constants/text';

interface GuestAccountCreationProps {
  guestEmail: string;
  guestName: string;
  onAccountCreated: () => void;
  onSkip: () => void;
}

const GuestAccountCreation: React.FC<GuestAccountCreationProps> = ({
  guestEmail,
  guestName,
  onAccountCreated,
  onSkip,
}) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleCreateAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      setError(TEXT_CONSTANTS.VALIDATION.PASSWORDS_DONT_MATCH);
      return;
    }

    if (password.length < 8) {
      setError(TEXT_CONSTANTS.VALIDATION.PASSWORD_TOO_SHORT);
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await api.post('/auth/create-guest-account', {
        name: guestName,
        email: guestEmail,
        password: password,
      });
      
      onAccountCreated();
    } catch (error: any) {
      setError(error.response?.data?.message || TEXT_CONSTANTS.ERRORS.FAILED_TO_CREATE);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6">
      <div className="flex items-start space-x-4">
        <div className="flex-shrink-0">
          <div className="w-10 h-10 bg-green-100 dark:bg-green-900/50 rounded-full flex items-center justify-center">
            <UserPlus className="w-5 h-5 text-green-600 dark:text-green-400" />
          </div>
        </div>
        
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Create Your Account
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Save your information for faster checkout next time! Your order details will be saved to your new account.
          </p>

          <form onSubmit={handleCreateAccount} className="space-y-4">
            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-3 py-2 rounded text-sm">
                {error}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {TEXT_CONSTANTS.PROFILE.NAME}
                </label>
                <input
                  type="text"
                  value={guestName}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md 
                           bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {TEXT_CONSTANTS.AUTH.EMAIL}
                </label>
                <input
                  type="email"
                  value={guestEmail}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md 
                           bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {TEXT_CONSTANTS.AUTH.PASSWORD} *
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-md 
                             bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
                             focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Enter password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {TEXT_CONSTANTS.AUTH.CONFIRM_PASSWORD} *
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-md 
                             bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
                             focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Confirm password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button
                type="submit"
                disabled={isLoading}
                className="flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-md 
                         hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 
                         disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isLoading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                {isLoading ? 'Creating Account...' : TEXT_CONSTANTS.AUTH.CREATE_ACCOUNT}
              </button>

              <button
                type="button"
                onClick={onSkip}
                className="flex items-center justify-center px-4 py-2 border border-gray-300 dark:border-gray-600 
                         text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 
                         focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
              >
                <X className="w-4 h-4 mr-2" />
                Skip for Now
              </button>
            </div>

            <p className="text-xs text-gray-500 dark:text-gray-400">
              By creating an account, you agree to our Terms of Service and Privacy Policy.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default GuestAccountCreation;